import getNewAuthToken from "./getAuthToken";
import setAuthToken from "./setAuthToken";
import logTokenState from "./logTokenState";
import { axiosAuth } from "./axios";
import { getSecretByName } from "../../redux/action/keyVaultAction";
import store from "../../redux/store";

class TokenManager {
    static instance = null;
    tokenData = null;
    staticToken = null;

    constructor() {
        if (!TokenManager.instance) {
            TokenManager.instance = this;
            this.loadTokenFromStorage();
        }
        return TokenManager.instance;
    }

    static getInstance() {
        if (!TokenManager.instance) {
            TokenManager.instance = new TokenManager();
        }
        return TokenManager.instance;
    }

    async initializeStaticToken() {
        if (this.staticToken) {
            return this.staticToken;
        }

        const resolvedToken = process.env.REACT_APP_IS_AZURE_VAULT
            ? process.env.REACT_APP_STATIC_TOKEN
            : await store.dispatch(getSecretByName("WEB-AUTH-TOKEN"));

        if (!resolvedToken) {
            throw new Error("Static token is not configured");
        }

        this.staticToken = resolvedToken;
        return this.staticToken;
    }

    loadTokenFromStorage() {
        const accessToken = localStorage.getItem("access_token");

        if (accessToken) {
            this.tokenData = {
                access_token: accessToken,
            };

            logTokenState(
                "Loaded token from storage",
                this.tokenData.access_token
            );
        }
    }

    saveTokenToStorage() {
        if (this.tokenData) {
            localStorage.setItem("access_token", this.tokenData.access_token);
        } else {
            localStorage.removeItem("access_token");
        }
    }

    async getValidToken() {
        if (this.tokenData?.access_token) {
            return this.tokenData.access_token;
        }

        const staticToken = await this.initializeStaticToken();
        setAuthToken(staticToken);

        return null; 
    }

    setTokens(tokenData) {
        if (!tokenData.access_token) {
            throw new Error("Invalid token data provided");
        }

        this.tokenData = {
            access_token: tokenData.access_token,
        };

        this.saveTokenToStorage();

        setAuthToken(tokenData.access_token, true);

        logTokenState("New tokens set", tokenData.access_token);
    }

    async refreshToken() {
        try {
            const staticToken = await this.initializeStaticToken();

            setAuthToken(staticToken);

            const newToken = await getNewAuthToken();

            this.setTokens({
                access_token: newToken.access_token,
            });

            return newToken;
        } catch (error) {
            // Only wipe tokens on real auth failures.
            // A 500 or timeout during refresh does not mean the tokens are invalid.
            const isAuthFailure =
                error?.response?.status === 401 ||
                error?.response?.status === 403 ||
                error?.message === "No refresh token available" ||
                error?.message === "invalid_grant";

            if (isAuthFailure) {
                this.expireSession();
            }

            throw error;
        }
    }

    getTokenData() {
        return this.tokenData ? { ...this.tokenData } : null;
    }

    async expireSession() {
        sessionStorage.setItem(
            "sessionExpired",
            "Your session has expired. Please sign in again."
        );

        await this.clearTokens();
    }

    async clearTokens(silent = false) {
        this.tokenData = null;

        this.saveTokenToStorage();

        delete axiosAuth.defaults.headers.common["Authorization"];

        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");

        sessionStorage.removeItem("access_token");
        sessionStorage.removeItem("refresh_token");

        const staticToken = await this.initializeStaticToken();

        setAuthToken(staticToken);

        if (!silent) {
            logTokenState("Tokens cleared", "");
            window.location.href = "/";
        }
    }
}

export default TokenManager;
